import axios from 'axios';
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { routes } from '../lib/routes';

// Create axios instance with default config
const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BACKEND_BASE_URL,
  timeout: 60000, // 60s timeout for AI/LLM operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - adds auth token and client metadata headers
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth-token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Client metadata headers for session tracking
    config.headers['X-Platform'] = 'web';
    config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
    config.headers['X-Language'] = navigator.language;
    config.headers['X-Screen'] = `${window.screen.width}x${window.screen.height}`;

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles common responses and errors
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          localStorage.removeItem('auth-token');
          window.location.href = routes.login;
          break;

        case 403:
          console.error('Access forbidden:', data);
          break;

        case 404:
          console.error('Resource not found:', data);
          break;

        case 422:
          console.error('Validation error:', data);
          break;

        case 500:
          console.error('Server error:', data);
          break;

        default:
          console.error(`HTTP ${status} error:`, data);
      }
    } else if (error.request) {
      console.error('Network error:', error.request);
    } else {
      console.error('Axios error:', error.message);
    }

    
    const safeError = {
      code: error.response?.status || 500,
      message: "Something went wrong. Please try again.",
      result: null,
    };

    // If server returned HTML
    if (error.response?.headers?.['content-type']?.includes('text/html')) {
      return Promise.reject(safeError);
    }

    // If backend returned a valid JSON object → pass through
    if (typeof error.response?.data === 'object') {
      return Promise.reject(error.response.data);
    }

    // If backend returned plain text, HTML, or unexpected data
    return Promise.reject(safeError);
  }
);

export default axiosInstance;

export type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError };

export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.get<T>(url, config),

  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    axiosInstance.post<T>(url, data, config),

  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    axiosInstance.put<T>(url, data, config),

  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) =>
    axiosInstance.patch<T>(url, data, config),

  delete: <T = any>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.delete<T>(url, config),
};

export interface ApiResponse<T = any> {
  code: number;
  message: string;
  result: T;
}
